<!--
  THE TRAILER, BECAUSE SOMEBODY ASKED FOR IT — AND IT IS OUR PLAYER, NOT THEIRS.

  The owner: "the trailer comes up as an overlay with YouTube on it. I just want
  to stream directly to my player if possible."

  We cannot put YouTube's bytes into our own `MediaSource` — there is no
  non-YouTube trailer for any of our titles and its bytes are not ours to fetch
  (docs/TRAILERS.md § A6, § H). What we CAN do is take its interface away
  completely, and that is what this is: a `controls=0` frame that takes no
  clicks at all, with our scrim, our type, our close button, our transport and
  our scrubber over it. Verified identically in Chromium and WebKit
  (docs/TRAILERS.md § B7): duration, position, seek, play, pause, mute and
  loaded-fraction all answer through the IFrame API, so every control here is
  real rather than decorative.

  A PRESS IS A DECISION. Every refusal elsewhere in this app — reduced motion,
  data saver, a film in progress, a background tab, the visit's byte budget —
  is about starting something nobody asked for, and none of them applies here.
  It plays with sound, at the size of the screen.

  IT CONTINUES, IT DOES NOT RESTART. `start` comes from `resumeSecFor()`, which
  knows where the trailer playing in the banner or in a tile had got to,
  including the second or two spent getting to this screen.

  It also takes the trailer slot, so opening this stops whatever was playing
  behind it. There is only ever one.
-->
<script lang="ts">
  import { untrack } from 'svelte';
  import Icon from './Icon.svelte';
  import Trailer from './Trailer.svelte';
  import { clock } from './format';
  import { claimTrailer, releaseTrailer, resumeSecFor } from './trailer.svelte';
  import { ENDED, PLAYING, type YtHandle } from './yt';

  interface Props {
    ytId: string;
    /** Ids to fall back to when the uploader has disabled embedding. */
    alternates?: string[];
    /** The 16:9 backdrop, held over the frame until the branding has gone. */
    still?: string | undefined;
    /** For the heading and the accessibility name. */
    name: string;
    /** "Watch movie" or "Watch series" — the caller knows which it is. */
    watchLabel?: string;
    /** Offered when the trailer ends. Omit and only "Back" is shown. */
    onwatch?: (() => void) | undefined;
    onclose: () => void;
  }

  let { ytId, alternates = [], still, name, watchLabel = 'Watch', onwatch, onclose }: Props = $props();

  /*
   * A trailer that has ended leaves a still frame and nothing to do with it.
   * The two things a person wants at that moment are the only two offered:
   * go back to the page, or start the thing they were just sold.
   */
  let over = $state(false);
  $effect(() => {
    void ytId;
    over = false;
  });

  const OWNER = 'trailer-sheet';

  /**
   * Where to start, read ONCE and before the claim.
   *
   * `resumeSecFor` answers from the trailer that is playing right now — which, a
   * microsecond after `claimTrailer`, is this one, at zero. So the order here is
   * the whole trick: ask where the last trailer had got to, and only then take
   * the slot from it. Untracked because this is a snapshot, not a subscription:
   * re-deriving it would wind the trailer back to its own start time on every
   * change of the shared state.
   */
  let start = $state(0);
  let armed = $state(false);

  $effect(() => {
    untrack(() => {
      start = resumeSecFor(ytId);
      claimTrailer(OWNER, ytId);
      armed = true;
    });
    return () => releaseTrailer(OWNER);
  });

  /* ── Our transport, driven through the frame ────────────────────────────── */

  let player = $state<YtHandle | null>(null);
  let playerState = $state(-1);
  let position = $state(0);
  let duration = $state(0);
  let buffered = $state(0);
  let muted = $state(false);
  /** True while a finger or a pointer is on the scrubber. */
  let scrubbing = $state(false);

  const playing = $derived(playerState === PLAYING);
  const ready = $derived(player !== null && duration > 0);

  /**
   * FOUR TIMES A SECOND, and only while there is something to read.
   *
   * The position lives on the other side of a postMessage bridge, so this is a
   * poll rather than an event — but it is a poll of three numbers, it stops the
   * moment the sheet closes, and it does not fight a finger that is dragging
   * the scrubber, which is the one case where our number is the truth and the
   * player's is stale.
   */
  $effect(() => {
    const handle = player;
    if (!handle) return;
    const read = (): void => {
      duration = handle.duration();
      buffered = handle.loaded();
      muted = handle.muted();
      if (!scrubbing) position = handle.time();
    };
    read();
    const timer = setInterval(read, 250);
    return () => clearInterval(timer);
  });

  function toggle(): void {
    if (!player) return;
    if (playing) player.pause();
    else player.play();
  }

  /** Gesture only — this is a click handler and that is the whole point. */
  function toggleSound(): void {
    if (!player) return;
    if (muted) player.unmute();
    else player.mute();
    muted = !muted;
  }

  function scrubTo(value: number): void {
    const next = Math.max(0, duration > 0 ? Math.min(value, duration) : value);
    position = next;
    player?.seek(next);
  }

  /** How far an arrow key moves. Ten presses to cross a two-minute trailer. */
  const NUDGE_SEC = 5;

  /**
   * THE ARROW KEYS MOVE FIVE SECONDS, NOT ONE STEP.
   *
   * The range's own step has to be fine enough that a click lands where the
   * pointer is, and measured, that makes an arrow key worth half a second —
   * against a playing video the position poll swallows it before the next
   * repaint, so the scrubber looked broken to a keyboard. Handling the keys
   * here decouples the two: pointer precision stays at the step, keyboard
   * precision is a real jump.
   */
  function onScrubKey(event: KeyboardEvent): void {
    const map: Record<string, number | null> = {
      ArrowLeft: -NUDGE_SEC,
      ArrowDown: -NUDGE_SEC,
      ArrowRight: NUDGE_SEC,
      ArrowUp: NUDGE_SEC
    };
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      scrubTo(event.key === 'Home' ? 0 : Math.max(0, duration - 1));
      return;
    }
    const delta = map[event.key];
    if (delta === undefined || delta === null) return;
    event.preventDefault();
    scrubTo(position + delta);
  }

  let closer = $state<HTMLButtonElement | null>(null);
  let dialog = $state<HTMLDialogElement | null>(null);
  $effect(() => {
    const el = dialog;
    if (!el) return;
    const previous = document.activeElement;
    el.showModal();
    closer?.focus();
    return () => {
      el.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  });

  function onkeydown(event: KeyboardEvent): void {
    // Interactive controls retain native Space activation.
    if (event.key === ' ' && !event.defaultPrevented &&
        !(event.target instanceof Element && event.target.closest('button, a[href], input, select, textarea, summary, [role="button"], [contenteditable]'))) {
      event.preventDefault();
      toggle();
    }
  }

  const elapsed = $derived(clock(position));
  const total = $derived(duration > 0 ? clock(duration) : '');
  const percent = $derived(duration > 0 ? Math.min(100, (position / duration) * 100) : 0);
  const bufferedPercent = $derived(Math.min(100, buffered * 100));
</script>

<svelte:window {onkeydown} />

<!-- Native modality contains Tab focus and makes the background inert. -->
<dialog bind:this={dialog} class="veil" data-dpad-scope aria-label="{name} trailer"
  oncancel={(event) => { event.preventDefault(); onclose(); }}>
  <!-- A real button rather than a click handler on the backdrop: it is a
       control, so it should be one, and then Escape is not the only way out for
       somebody who cannot use a pointer. -->
  <button type="button" class="behind" tabindex="-1" aria-label="Close the trailer" onclick={onclose}></button>
  <div class="box">
    <div class="head">
      <p class="name">{name}</p>
      <button type="button" class="close" bind:this={closer} aria-label="Close the trailer" onclick={onclose}>
        <Icon name="close" size={18} />
      </button>
    </div>
    <div class="screen">
      {#if armed}
        <Trailer
          {ytId}
          {alternates}
          {still}
          {start}
          muted={false}
          holdMs={0}
          tappable
          label="{name} trailer"
          onended={() => (over = true)}
          onplayer={(handle) => (player = handle)}
          onstate={(s) => (playerState = s)}
        />
      {/if}

      <!--
        THE END OF A TRAILER IS A DECISION POINT, and it used to be a still frame
        with nothing on it. Two choices, because there are only two: go back to
        the page, or start the thing the trailer just sold you.
      -->
      {#if over}
        <div class="over">
          <p>{name}</p>
          <div class="acts">
            {#if onwatch}
              <button type="button" class="go" onclick={onwatch}>
                <Icon name="play" size={17} />{watchLabel}
              </button>
            {/if}
            <button type="button" class="back" onclick={onclose}>Back</button>
          </div>
        </div>
      {/if}
    </div>

    <!--
      OUR TRANSPORT. Reserved whether or not the player has answered yet, so the
      sheet does not change height when the duration arrives — and disabled
      rather than absent, so nothing moves when it becomes real.
    -->
    <div class="bar" class:live={ready}>
      <button
        type="button"
        class="key"
        disabled={!ready}
        aria-label={playing ? 'Pause the trailer' : 'Play the trailer'}
        onclick={toggle}
      >
        <Icon name={playing ? 'pause' : 'play'} size={17} />
      </button>

      <span class="time">{elapsed}</span>

      <div class="seek">
        <span class="rail" aria-hidden="true">
          <i class="buffered" style:width="{bufferedPercent}%"></i>
          <i class="played" style:width="{percent}%"></i>
        </span>
        <input
          type="range"
          min="0"
          max={duration > 0 ? duration : 1}
          step="0.5"
          value={position}
          disabled={!ready}
          aria-label="Seek within the trailer"
          aria-valuetext="{elapsed} of {total}"
          onpointerdown={() => (scrubbing = true)}
          onpointerup={() => (scrubbing = false)}
          oninput={(event) => scrubTo(Number(event.currentTarget.value))}
          onkeydown={onScrubKey}
          onchange={() => (scrubbing = false)}
        />
      </div>

      <span class="time right">{total}</span>

      <button
        type="button"
        class="key"
        disabled={!ready}
        aria-pressed={muted}
        aria-label={muted ? 'Unmute the trailer' : 'Mute the trailer'}
        onclick={toggleSound}
      >
        <Icon name={muted ? 'mute' : 'speaker'} size={17} />
      </button>
    </div>

    {#if playerState === ENDED}
      <p class="done" role="status">That is the whole trailer.</p>
    {/if}
  </div>
</dialog>

<style>
  /* Over the finished frame, not beside it: the picture is the ground. */
  .over {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    padding: var(--space-5);
    background: color-mix(in oklab, var(--bg) 76%, transparent);
    backdrop-filter: blur(10px) saturate(1.2);
    animation: overIn var(--dur) var(--ease-out);
  }
  @keyframes overIn {
    from {
      opacity: 0;
    }
  }
  .over p {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    text-align: center;
    text-wrap: balance;
  }
  .over .acts {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
    justify-content: center;
  }
  .over button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: var(--hit);
    padding-inline: var(--space-5);
    border-radius: var(--radius-pill);
    border: 1px solid transparent;
    font: inherit;
    font-weight: var(--weight-semi);
    cursor: pointer;
  }
  .over .go {
    background: var(--accent);
    color: var(--accent-ink);
  }
  .over .back {
    background: color-mix(in oklab, var(--text) 12%, transparent);
    border-color: var(--line);
    color: var(--text);
  }
  .over button:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    .over {
      animation: none;
    }
  }

  .veil {
    margin: 0;
    border: 0;
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    color: var(--text);
    position: fixed;
    inset: 0;
    z-index: 90;
    display: grid;
    place-items: center;
    padding: max(var(--space-4), var(--safe-t)) max(var(--space-4), var(--safe-r))
      max(var(--space-4), var(--safe-b)) max(var(--space-4), var(--safe-l));
    background: rgb(4 6 10 / 0.86);
    backdrop-filter: blur(6px);
  }

  .behind {
    position: absolute;
    inset: 0;
    background: none;
    cursor: default;
  }

  .box {
    position: relative;
    width: min(100%, 1100px);
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-bottom: var(--space-2);
  }
  .name {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: var(--text-md);
    font-weight: var(--weight-semi);
  }
  .close {
    display: grid;
    place-content: center;
    flex: none;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-line);
    background: var(--glass-fill);
    color: var(--text-dim);
  }
  .close:hover {
    color: var(--text);
    background: var(--glass-fill-hi);
  }
  .close:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  /* The 16:9 hole the trailer fills. It does its own letterboxing inside it. */
  .screen {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: var(--radius-lg);
    overflow: hidden;
    background: #000;
    box-shadow: 0 30px 80px rgb(0 0 0 / 0.6);
  }

  .bar {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--hit);
    margin-top: var(--space-2);
    opacity: 0.45;
    transition: opacity var(--dur) var(--ease);
  }
  .bar.live {
    opacity: 1;
  }

  .key {
    display: grid;
    place-content: center;
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-line);
    background: var(--glass-fill);
    color: var(--text);
  }
  .key:hover:not(:disabled) {
    background: var(--glass-fill-hi);
  }
  .key:disabled {
    color: var(--text-faint);
    cursor: default;
  }
  .key:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  .time {
    flex: none;
    min-width: 4ch;
    color: var(--text-dim);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums;
  }
  .time.right {
    text-align: right;
  }

  /*
   * THE PICTURE IS OURS AND THE HIT TEST IS THE INPUT'S. A styled `<input
   * type="range">` is the only scrubber that a keyboard and a screen reader
   * both already understand, so it stays — invisible, on top, full size — and
   * the two bars underneath are what a person sees.
   */
  .seek {
    position: relative;
    flex: 1;
    min-width: 0;
    height: var(--hit);
    display: grid;
    align-items: center;
  }
  .rail {
    position: relative;
    display: block;
    height: 4px;
    border-radius: var(--radius-pill);
    background: color-mix(in oklab, var(--text) 18%, transparent);
    overflow: hidden;
  }
  .rail i {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    display: block;
  }
  .buffered {
    background: color-mix(in oklab, var(--text) 26%, transparent);
  }
  .played {
    background: var(--accent);
  }
  .seek input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }
  .seek input:disabled {
    cursor: default;
  }
  .seek:focus-within .rail {
    outline: 2px solid var(--focus-ring);
    outline-offset: 4px;
  }

  .done {
    margin-top: var(--space-2);
    color: var(--text-faint);
    font-size: var(--text-xs);
  }

  @media (max-width: 520px) {
    .time.right {
      display: none;
    }
  }
  @media (pointer: coarse) {
    .key { position: relative; }
    .key::after { content: ""; position: absolute; width: max(100%, var(--hit)); height: max(100%, var(--hit)); top: 50%; left: 50%; transform: translate(-50%, -50%); }
  }
</style>
